import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ProductService, ResourceItem } from '@app/services/product.service';
import { SrActivityGet, SrOperationService } from '@app/services/sr-operation.service';

@Component({ selector: 'tt-sr-activity', templateUrl: './sr-activity.component.html', styleUrls: ['./sr-activity.component.css'], standalone: false })
export class SrActivityComponent implements OnInit {
    public page?: SrActivityGet;
    public activities: Item[] = [];
    public searching = false;
    public error = '';
    private resources: ResourceItem[] = [];
    constructor(private readonly operation: SrOperationService, private readonly product: ProductService, private readonly remember: RememberService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) { this.navigator = useNavigatorFor(navigator, this); }
    public async ngOnInit(): Promise<void> {
        try { this.page = await this.operation.getActivity(); await this.loadActivities(); } catch (error) { this.showError(error); }
    }
    private async loadActivities(): Promise<void> {
        if (!this.page || this.page.resource_searchtext == null || this.page.only_this_resource == null || !this.page.datetime_fom?.trim()) return;
        this.activities = [];
        try { this.activities = await this.operation.getActivityList(this.page); } catch (error) { this.showError(error); }
    }
    public searchResources = async (params?: { value?: string }): Promise<ResourceItem[]> => {
        this.searching = true;
        try {
            this.resources = await this.product.searchResources({ resource_searchtext: params?.value ?? this.page?.resource_searchtext ?? '' });
            return this.resources;
        } catch (error) { this.showError(error); return []; }
        finally { this.searching = false; }
    };
    public async onResourceTextChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.resource_searchtext) return;
        const previous = this.page.resource_searchtext;
        if (previous === undefined) { this.page.resource_searchtext = value; return; }
        if (value === '') { await this.clearResource(); return; }
        this.page.resource_searchtext = value;
        for (const resource of this.resources) if (resource.item_name === value) {
            this.page.resource_id = resource.item_id;
            await this.loadActivities();
        }
    }
    public async onResourceSelected(resource: ResourceItem): Promise<void> {
        if (!this.page || (this.page.resource_id === resource.item_id && this.page.resource_searchtext === resource.item_name)) return;
        this.page.resource_id = resource.item_id;
        this.page.resource_searchtext = resource.item_name;
        await this.loadActivities();
    }
    public async clearResource(): Promise<void> {
        if (!this.page || (this.page.resource_id === '' && this.page.resource_searchtext === '')) return;
        this.page.resource_id = ''; this.page.resource_searchtext = '';
        await this.loadActivities();
    }
    public async onDateChanged(value: string): Promise<void> {
        if (!this.page) return;
        const previous = this.page.datetime_fom; this.page.datetime_fom = value;
        if (previous === undefined || value === previous || value.length > 16 || previous.length > 16) return;
        await this.loadActivities();
    }
    public async onOnlyResourceChanged(value: string): Promise<void> {
        if (!this.page) return;
        const previous = this.page.only_this_resource; this.page.only_this_resource = value;
        if (previous === undefined || value === previous || value.length > 16 || previous.length > 16) return;
        const load = this.loadActivities();
        // The legacy checkbox reloads even for empty values, but remembers only nonempty values.
        if (value.trim()) {
            try { await this.remember.remember('w_sractivity.only_this_resource', value); } catch (error) { this.showError(error); }
        }
        await load;
    }
    public openItem({ item, event }: { item: Item; event: MouseEvent }): void {
        if (item.item_state === 'customaction') { const action = item['item_action']; if (typeof action === 'function') action(item); return; }
        if (!item.item_state || item.item_path === '') return;
        const to = ['sractivity', 'srfindreport', 'srreportmenu', 'sroperation', 'srfollowup', 'srtravelbill'].includes(item.item_state) ? `${item.item_state}-test` : item.item_state;
        const target = { to, parms: item.item_parms };
        if (event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
